import React from 'react'
import { useParams } from 'react-router-dom'

import { Form, Section } from '@factorialco/deprecated-design-system'
import { ContactEditPageDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import BankInfoForm from 'modules/finance/components/Contacts/BankInfoForm'
import ContactAddressForm from 'modules/finance/components/Contacts/ContactAddressForm'
import ContactDetailsForm from 'modules/finance/components/Contacts/ContactDetailsForm'
import LedgerAccountSelectorForm from 'modules/finance/components/Contacts/LedgerAccountSelectorForm'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const ContactEditPage: React.FC = () => {
  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'contact_edit',
    })
  }, [])

  const { contactId } = useParams()

  const { data, loading, error } = useQuery(ContactEditPageDocument, {
    variables: {
      id: Number(contactId),
    },
    fetchPolicy: 'no-cache',
  })
  const contact = data?.finance?.contact
  const legalEntities = data?.companies?.legalEntities ?? []
  const canManageLedgerAccounts =
    data?.finance?._policies?.accountingManageAccountResources?.can ?? false
  const seeSalesLedgerAccounts = data?.finance?._policies?.accountingSeeSalesAccounts?.can ?? false
  const seePurchaseLedgerAccounts =
    data?.finance?._policies?.accountingSeePurchaseAccounts?.can ?? false

  const canManageContacts = data?.finance._policies.contactsCreateOrUpdate.can ?? false
  const canManageContactsPaymentData =
    data?.finance._policies.contactsManagePaymentData.can ?? false

  return (
    <ResponsivePage.Body
      authorized={canManageContacts}
      error={error}
      loading={loading}
      variant="full"
    >
      <>
        <Section
          title={i18n.t('finance.contacts.contact_details.contact_details.title')}
          description={i18n.t('finance.contacts.contact_details.contact_details.description')}
          illustration="multiple-buildings"
          paddingX={null}
        >
          <Form withBackground>{contact && <ContactDetailsForm contact={contact} />}</Form>
        </Section>

        <Section
          title={i18n.t('finance.contacts.contact_details.address.title')}
          description={i18n.t('finance.contacts.contact_details.address.description')}
          illustration="location-pin"
          paddingX={null}
        >
          <Form withBackground>{contact && <ContactAddressForm contact={contact} />}</Form>
        </Section>
      </>

      {canManageContactsPaymentData && (
        <Section
          title={i18n.t('finance.contacts.contact_details.bank_information.title')}
          description={i18n.t('finance.contacts.contact_details.bank_information.description')}
          illustration="credit-card"
          paddingX={null}
        >
          <Form withBackground>{contact && <BankInfoForm vendor={contact} />}</Form>
        </Section>
      )}

      {canManageLedgerAccounts && contact && (
        <Section
          title={i18n.t('finance.contacts.contact_details.ledger_account.title')}
          description={i18n.t('finance.contacts.contact_details.ledger_account.description')}
          illustration="money-spreadsheet"
          paddingX={null}
        >
          <Form withBackground>
            <LedgerAccountSelectorForm
              legalEntities={legalEntities}
              contact={contact}
              canManage
              seeSalesLedgerAccounts={seeSalesLedgerAccounts}
              seePurchaseLedgerAccounts={seePurchaseLedgerAccounts}
            />
          </Form>
        </Section>
      )}
    </ResponsivePage.Body>
  )
}

export default ContactEditPage
